import createDOMPurify, {type WindowLike} from 'dompurify'

export const UBL_STYLESHEET_URL = '/assets/ubl-peppol.sef.json'
export const SAXON_RUNTIME_URL = '/assets/SaxonJS2.rt.js'

const SANITIZE_CONFIG = {WHOLE_DOCUMENT: true, FORBID_TAGS: ['a']}

export class UblRenderError extends Error {
    constructor(message: string) {
        super(message)
        this.name = 'UblRenderError'
    }
}

export function parseUblXml(xmlString: string): Document {
    const document = new DOMParser().parseFromString(xmlString, 'application/xml')
    const parserError = document.getElementsByTagName('parsererror')
    if (parserError.length > 0) {
        const detail = parserError[0].textContent?.replace(/\s+/g, ' ').trim()
        throw new UblRenderError(detail || 'The provided document is not well-formed XML.')
    }
    return document
}

interface TransformArgs {
    sourceText: string
    stylesheetLocation?: string
    stylesheetInternal?: unknown
    sanitizerWindow?: WindowLike
}

export function sanitizeUblHtml(html: string, sanitizerWindow?: WindowLike): string {
    const targetWindow = sanitizerWindow ?? (typeof window === 'undefined' ? undefined : (window as unknown as WindowLike))
    if (!targetWindow) {
        throw new UblRenderError('No browser environment available to sanitize the rendered document.')
    }
    const purifier = createDOMPurify(targetWindow)
    if (!purifier.isSupported) {
        throw new UblRenderError('The current environment cannot sanitize the rendered document.')
    }
    return `<!DOCTYPE html>\n${purifier.sanitize(html, SANITIZE_CONFIG)}`
}

export async function transformUblToHtml(saxon: SaxonJSApi, args: TransformArgs): Promise<string> {
    const {sourceText, stylesheetInternal, stylesheetLocation = UBL_STYLESHEET_URL} = args
    const result = await saxon.transform(
        {
            ...(stylesheetInternal ? {stylesheetInternal} : {stylesheetLocation}),
            sourceText,
            destination: 'serialized',
        },
        'async',
    )
    const html = (result as SaxonTransformResult).principalResult
    if (!html) {
        throw new UblRenderError('The stylesheet produced no output for this document.')
    }
    return sanitizeUblHtml(html, args.sanitizerWindow)
}

let runtimePromise: Promise<SaxonJSApi> | undefined

export function loadSaxonRuntime(src: string = SAXON_RUNTIME_URL): Promise<SaxonJSApi> {
    if (typeof window !== 'undefined' && window.SaxonJS) {
        return Promise.resolve(window.SaxonJS)
    }
    if (runtimePromise) {
        return runtimePromise
    }
    runtimePromise = new Promise<SaxonJSApi>((resolve, reject) => {
        const script = document.createElement('script')
        script.src = src
        script.async = true
        script.dataset.saxonRuntime = 'true'
        script.onload = () => {
            if (window.SaxonJS) {
                resolve(window.SaxonJS)
            } else {
                runtimePromise = undefined
                reject(new UblRenderError('The SaxonJS runtime loaded but its global was not found.'))
            }
        }
        script.onerror = () => {
            runtimePromise = undefined
            reject(new UblRenderError('Failed to load the SaxonJS runtime.'))
        }
        document.head.appendChild(script)
    })
    return runtimePromise
}
